import { useState, useEffect } from "react";
import { DashboardLayout } from "../components/dashboard/dashboard-layout";
import { PageHeader } from "../components/dashboard/page-header";
import {
  Search,
  Filter,
  Download,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  CreditCard,
  Zap,
  TrendingUp,
  Calendar,
  Eye,
  MessageSquare,
  Plus,
  Minus,
  RefreshCw
} from "lucide-react";

import apiClient from "../services/api";

interface CreditTransaction {
  _id: string;
  type: 'usage' | 'refill' | 'bonus' | 'adjustment';
  platform: string;
  creditsUsed: number;
  creditsAdded: number;
  status: 'success' | 'failed' | 'pending' | 'completed';
  description: string;
  messageContent?: string;
  generatedResponse?: string;
  commentId?: string;
  createdAt: string;
  metadata?: any;
}

interface TransactionData {
  transactions: CreditTransaction[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    limit: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  summary: {
    totalCreditsUsed: number;
    totalCreditsAdded: number;
    netCredits: number;
    platformBreakdown: any[];
    typeBreakdown: any[];
  };
  currentBalance: {
    totalCredits: number;
    usedCredits: number;
    availableCredits: number;
  };
}

export default function TransactionsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [dateRange, setDateRange] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [transactionData, setTransactionData] = useState<TransactionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<CreditTransaction | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarMonth, setCalendarMonth] = useState(new Date());

  // Get date range for filtering
  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    switch (dateRange) {
      case 'today':
        return {
          startDate: today.toISOString(),
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString()
        };
      case 'yesterday':
        const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
        return {
          startDate: yesterday.toISOString(),
          endDate: today.toISOString()
        };
      case 'week':
        const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        return {
          startDate: weekAgo.toISOString(),
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString()
        };
      case 'month':
        const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
        return {
          startDate: monthAgo.toISOString(),
          endDate: new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString()
        };
      case 'custom':
        if (customStartDate && customEndDate) {
          return {
            startDate: new Date(customStartDate).toISOString(),
            endDate: new Date(customEndDate + 'T23:59:59').toISOString()
          };
        }
        return null;
      default:
        return null;
    }
  };

  // Fetch transactions from backend
  const fetchTransactions = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: '10',
        ...(selectedStatus !== 'all' && { status: selectedStatus }),
        ...(selectedType !== 'all' && { type: selectedType }),
        ...(selectedPlatform !== 'all' && { platform: selectedPlatform }),
        ...(searchQuery && { search: searchQuery }),
      });

      // Add date range if selected
      const dateRangeParams = getDateRange();
      if (dateRangeParams) {
        params.append('startDate', dateRangeParams.startDate);
        params.append('endDate', dateRangeParams.endDate);
      }

      const response = await apiClient.get(`/backend/api/credits/transactions?${params}`);

      if (response.data.success) {
        setTransactionData(response.data.data);
      } else {
        setError(response.data.error || 'Failed to fetch transactions');
      }
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      setError(error.response?.data?.error || 'Failed to fetch transactions');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch transactions on component mount and when filters change
  useEffect(() => {
    fetchTransactions();
  }, [currentPage, selectedStatus, selectedType, selectedPlatform, searchQuery, dateRange, customStartDate, customEndDate]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [selectedStatus, selectedType, selectedPlatform, searchQuery]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  // Get platform icon component
  const getPlatformIcon = (platform: string) => {
    const iconClass = "h-4 w-4";
    switch (platform.toLowerCase()) {
      case 'instagram': return <MessageSquare className={iconClass} />;
      case 'judgeme': return <TrendingUp className={iconClass} />;
      case 'yotpo': return <MessageSquare className={iconClass} />;
      case 'shopify': return <CreditCard className={iconClass} />;
      case 'system': return <Zap className={iconClass} />;
      default: return <MessageSquare className={iconClass} />;
    }
  };

  // Get type color
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'usage': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
      case 'refill': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'bonus': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'adjustment': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  // Use real transaction data or show loading/error state
  const displayTransactions = transactionData?.transactions || [];
  const pagination = transactionData?.pagination || {
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNext: false,
    hasPrev: false
  };

  // Calculate accurate totals from actual transaction data
  const calculateTotalsFromTransactions = () => {
    if (!displayTransactions || displayTransactions.length === 0) {
      return {
        totalCreditsUsed: 0,
        totalCreditsAdded: 0,
        netCredits: 0
      };
    }

    const totals = displayTransactions.reduce((acc, transaction) => {
      const creditsUsed = transaction.creditsUsed || 0;
      const creditsAdded = transaction.creditsAdded || 0;

      return {
        totalCreditsUsed: acc.totalCreditsUsed + creditsUsed,
        totalCreditsAdded: acc.totalCreditsAdded + creditsAdded,
        netCredits: acc.netCredits + (creditsAdded - creditsUsed)
      };
    }, {
      totalCreditsUsed: 0,
      totalCreditsAdded: 0,
      netCredits: 0
    });

    return totals;
  };

  const calculatedTotals = calculateTotalsFromTransactions();



  // Handle pagination navigation
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  // Handle view transaction details
  const handleViewTransaction = (transaction: CreditTransaction) => {
    setSelectedTransaction(transaction);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedTransaction(null);
  };

  // Refresh data
  const refreshData = () => {
    fetchTransactions();
  };



  // Handle calendar toggle
  const handleDateClick = () => {
    setShowCalendar(!showCalendar);
  };

  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatCalendarDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  const handleDateSelect = (day: number) => {
    const newDate = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
    setSelectedDate(newDate);
    setCustomStartDate(newDate.toISOString().split('T')[0]);
    setCustomEndDate(newDate.toISOString().split('T')[0]);
    setDateRange('custom');
    setShowCalendar(false);
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCalendarMonth(prev => {
      const newMonth = new Date(prev);
      if (direction === 'prev') {
        newMonth.setMonth(prev.getMonth() - 1);
      } else {
        newMonth.setMonth(prev.getMonth() + 1);
      }
      return newMonth;
    });
  };

  // Clear all filters
  const clearFilters = () => {
    setSelectedType('all');
    setSelectedPlatform('all');
    setSelectedStatus('all');
    setDateRange('all');
    setCustomStartDate('');
    setCustomEndDate('');
    setSearchQuery('');
    setSearchInput('');
  };

  // Check if any filters are active
  const hasActiveFilters = selectedType !== 'all' || selectedPlatform !== 'all' || selectedStatus !== 'all' || dateRange !== 'all' || searchQuery !== '' || searchInput !== '';

  // Export transactions to CSV
  const exportTransactions = async () => {
    try {
      const params = new URLSearchParams({
        ...(selectedStatus !== 'all' && { status: selectedStatus }),
        ...(selectedType !== 'all' && { type: selectedType }),
        ...(selectedPlatform !== 'all' && { platform: selectedPlatform }),
        ...(searchQuery && { search: searchQuery }),
        limit: '1000', // Export more records
      });

      const response = await apiClient.get(`/api/credits/transactions?${params}`);

      if (response.data.success) {
        const transactions = response.data.data.transactions;

        // Convert to CSV
        const csvHeaders = [
          'Transaction ID',
          'Type',
          'Platform',
          'Credits Used',
          'Credits Added',
          'Status',
          'Date',
          'Time',
          'Description',
          'Customer Message',
          'Generated Response',
          'Comment ID'
        ];

        const csvRows = transactions.map((transaction: CreditTransaction) => {
          const dateTime = formatDate(transaction.createdAt);
          return [
            transaction._id.slice(-8).toUpperCase(),
            transaction.type,
            transaction.platform,
            transaction.creditsUsed || 0,
            transaction.creditsAdded || 0,
            transaction.status,
            dateTime.date,
            dateTime.time,
            transaction.description || '',
            transaction.messageContent || '',
            transaction.generatedResponse || '',
            transaction.commentId || ''
          ];
        });

        // Create CSV content
        const csvContent = [
          csvHeaders.join(','),
          ...csvRows.map((row: any[]) =>
            row.map((field: any) =>
              typeof field === 'string' && field.includes(',')
                ? `"${field.replace(/"/g, '""')}"`
                : field
            ).join(',')
          )
        ].join('\n');

        // Download CSV
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `credit-transactions-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export transactions. Please try again.');
    }
  };

  return (
    <DashboardLayout>
      <div className="relative">
        <PageHeader
          title="Transactions"
          description="View and manage all your transactions"
          onRefresh={refreshData}
          onDateClick={handleDateClick}
        />



        {/* Shadcn-style Calendar Dropdown */}
        {showCalendar && (
          <div className="fixed inset-0 z-40" onClick={() => setShowCalendar(false)}>
            <div
              className="absolute top-32 right-4 z-50 bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 rounded-md shadow-lg p-3 min-w-[280px]"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Calendar Header */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={() => navigateMonth('prev')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-sm transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatCalendarDate(calendarMonth)}
              </div>
              <button
                onClick={() => navigateMonth('next')}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-sm transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Days of Week */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
                <div key={day} className="text-xs font-medium text-gray-500 dark:text-gray-400 text-center p-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1">
              {/* Empty cells for days before month starts */}
              {Array.from({ length: getFirstDayOfMonth(calendarMonth) }).map((_, index) => (
                <div key={`empty-${index}`} className="p-2"></div>
              ))}

              {/* Days of the month */}
              {Array.from({ length: getDaysInMonth(calendarMonth) }).map((_, index) => {
                const day = index + 1;
                const isSelected = selectedDate.getDate() === day &&
                                 selectedDate.getMonth() === calendarMonth.getMonth() &&
                                 selectedDate.getFullYear() === calendarMonth.getFullYear();
                const isToday = new Date().getDate() === day &&
                               new Date().getMonth() === calendarMonth.getMonth() &&
                               new Date().getFullYear() === calendarMonth.getFullYear();

                return (
                  <button
                    key={day}
                    onClick={() => handleDateSelect(day)}
                    className={`
                      p-2 text-sm rounded-sm transition-colors hover:bg-gray-100 dark:hover:bg-gray-800
                      ${isSelected
                        ? 'bg-gray-900 dark:bg-gray-50 text-white dark:text-gray-900'
                        : isToday
                        ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100'
                        : 'text-gray-700 dark:text-gray-300'
                      }
                    `}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {/* Quick Actions */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 dark:border-gray-800">
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    setDateRange('today');
                    setShowCalendar(false);
                  }}
                  className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                >
                  Today
                </button>
                <button
                  onClick={() => {
                    setDateRange('week');
                    setShowCalendar(false);
                  }}
                  className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                >
                  Week
                </button>
              </div>
              <button
                onClick={() => setShowCalendar(false)}
                className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
            </div>
          </div>
        )}
      </div>

      {/* Credit Stats Cards */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-3 mb-6">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Available Credits</h3>
            <div className="p-2 rounded-full bg-green-100 dark:bg-green-900/30">
              <Zap className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">
              {isLoading ? '...' : (transactionData?.currentBalance.availableCredits || 0)}
            </span>
            <span className="ml-2 text-sm font-medium text-gray-500 dark:text-gray-400">
              / {isLoading ? '...' : (transactionData?.currentBalance.totalCredits || 0)}
            </span>
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">Credits remaining</div>
          <div className="mt-2 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all duration-300"
              style={{
                width: transactionData ?
                  `${Math.max(5, (transactionData.currentBalance.availableCredits / transactionData.currentBalance.totalCredits) * 100)}%` :
                  '0%'
              }}
            ></div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Credits Used</h3>
            <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30">
              <Minus className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">
              {isLoading ? '...' : calculatedTotals.totalCreditsUsed}
            </span>
            <span className="ml-2 text-sm font-medium flex items-center text-red-600 dark:text-red-400">
              <MessageSquare className="h-3 w-3 mr-1" />
              replies
            </span>
          </div>
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Total credits consumed
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Credits Added</h3>
            <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Plus className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="flex items-baseline">
            <span className="text-3xl font-bold">
              {isLoading ? '...' : calculatedTotals.totalCreditsAdded}
            </span>
          </div>
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Total credits purchased/added
          </div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <div className="relative w-full sm:w-64 md:w-80">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            placeholder="Search transactions..."
            className="pl-10 pr-4 py-2 w-full rounded-lg bg-white dark:bg-gray-900 text-sm transition-all duration-300 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700 border border-gray-200 dark:border-gray-800"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select
            className="px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700"
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
          >
            <option value="all">All Types</option>
            <option value="usage">Usage</option>
            <option value="refill">Refill</option>
            <option value="bonus">Bonus</option>
            <option value="adjustment">Adjustment</option>
          </select>

          <select
            className="px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700"
            value={selectedPlatform}
            onChange={(e) => setSelectedPlatform(e.target.value)}
          >
            <option value="all">All Platforms</option>
            <option value="instagram">Instagram</option>
            <option value="judgeme">Judge.me</option>
            <option value="yotpo">Yotpo</option>
            <option value="shopify">Shopify</option>
            <option value="system">System</option>
          </select>

          <select
            className="px-3 py-2 rounded-lg bg-white dark:bg-gray-900 text-sm border border-gray-200 dark:border-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-300 dark:focus:ring-gray-700"
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30 transition-all duration-200 text-sm"
              title="Clear all filters"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}

          <button
            onClick={exportTransactions}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
            disabled={isLoading}
          >
            <Download className="h-4 w-4" />
            <span className="hidden sm:inline">{isLoading ? 'Loading...' : 'Export'}</span>
          </button>
        </div>
      </div>

      {/* Credit Transactions Table */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800 mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-800">
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Transaction ID</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Platform</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Credits</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Date</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Message/Response</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-gray-500 dark:text-gray-400">
                    Loading transactions...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-red-500">
                    Error: {error}
                  </td>
                </tr>
              ) : displayTransactions.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-gray-500 dark:text-gray-400">
                    No transactions found
                  </td>
                </tr>
              ) : (
                displayTransactions.map((transaction) => {
                  const dateTime = formatDate(transaction.createdAt);
                  return (
                    <tr key={transaction._id} className="border-b border-gray-200 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="py-3 px-4 font-medium text-xs">
                        {transaction._id.slice(-8).toUpperCase()}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(transaction.type)}`}>
                          {transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center">
                          <span className="mr-2 text-gray-500 dark:text-gray-400">{getPlatformIcon(transaction.platform)}</span>
                          <span className="capitalize">{transaction.platform}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col">
                          {transaction.creditsUsed > 0 && (
                            <span className="text-red-600 dark:text-red-400 text-sm">
                              -{transaction.creditsUsed}
                            </span>
                          )}
                          {transaction.creditsAdded > 0 && (
                            <span className="text-green-600 dark:text-green-400 text-sm">
                              +{transaction.creditsAdded}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          transaction.status === 'success'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : transaction.status === 'pending'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                            : transaction.status === 'failed'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                        }`}>
                          {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div>{dateTime.date}</div>
                        <div className="text-gray-500 dark:text-gray-400">{dateTime.time}</div>
                      </td>
                      <td className="py-3 px-4 max-w-xs">
                        <div className="space-y-1">
                          {transaction.messageContent && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 truncate" title={transaction.messageContent}>
                              <strong>Message:</strong> {transaction.messageContent.substring(0, 50)}...
                            </div>
                          )}
                          {transaction.generatedResponse && (
                            <div className="text-xs text-blue-600 dark:text-blue-400 truncate" title={transaction.generatedResponse}>
                              <strong>Response:</strong> {transaction.generatedResponse.substring(0, 50)}...
                            </div>
                          )}
                          {transaction.description && !transaction.messageContent && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 truncate" title={transaction.description}>
                              {transaction.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <button
                          className="inline-flex items-center justify-center h-8 w-8 rounded-full text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
                          title="View Details"
                          onClick={() => handleViewTransaction(transaction)}
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {transactionData && transactionData.pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 dark:border-gray-800">
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Showing <span className="font-medium">{((transactionData.pagination.currentPage - 1) * transactionData.pagination.limit) + 1}</span> to <span className="font-medium">{Math.min(transactionData.pagination.currentPage * transactionData.pagination.limit, transactionData.pagination.totalCount)}</span> of <span className="font-medium">{transactionData.pagination.totalCount}</span> results
            </div>
            <div className="flex items-center gap-2">
              <button
                className={`p-2 rounded-lg border border-gray-200 dark:border-gray-800 ${!transactionData.pagination.hasPrev ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={!transactionData.pagination.hasPrev}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>

              {Array.from({ length: Math.min(5, transactionData.pagination.totalPages) }, (_, i) => {
                const startPage = Math.max(1, transactionData.pagination.currentPage - 2);
                const page = startPage + i;
                if (page > transactionData.pagination.totalPages) return null;
                return (
                  <button
                    key={page}
                    className={`px-3 py-1 rounded-lg ${currentPage === page ? 'bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-800'}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                className={`p-2 rounded-lg border border-gray-200 dark:border-gray-800 ${!transactionData.pagination.hasNext ? 'text-gray-400 dark:text-gray-600 cursor-not-allowed' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'}`}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, transactionData.pagination.totalPages))}
                disabled={!transactionData.pagination.hasNext}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Transaction Details Modal */}
      {showModal && selectedTransaction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-800">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Transaction Details
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  ID: {selectedTransaction._id.slice(-8).toUpperCase()}
                </p>
              </div>
              <button
                onClick={closeModal}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Transaction Overview */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Type</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                        {selectedTransaction.type}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getTypeColor(selectedTransaction.type)}`}>
                      {selectedTransaction.type.charAt(0).toUpperCase() + selectedTransaction.type.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Platform</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white capitalize">
                        {selectedTransaction.platform}
                      </p>
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">
                      {getPlatformIcon(selectedTransaction.platform)}
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Credits</p>
                      <div className="flex flex-col">
                        {selectedTransaction.creditsUsed > 0 && (
                          <span className="text-lg font-semibold text-red-600 dark:text-red-400">
                            -{selectedTransaction.creditsUsed}
                          </span>
                        )}
                        {selectedTransaction.creditsAdded > 0 && (
                          <span className="text-lg font-semibold text-green-600 dark:text-green-400">
                            +{selectedTransaction.creditsAdded}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-2xl">
                      {selectedTransaction.creditsUsed > 0 ? <Minus className="h-6 w-6 text-red-500" /> : <Plus className="h-6 w-6 text-green-500" />}
                    </div>
                  </div>
                </div>
              </div>

              {/* Status and Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Status</p>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    selectedTransaction.status === 'completed'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                      : selectedTransaction.status === 'pending'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                      : selectedTransaction.status === 'failed'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                  }`}>
                    {selectedTransaction.status.charAt(0).toUpperCase() + selectedTransaction.status.slice(1)}
                  </span>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Date & Time</p>
                  <div className="text-gray-900 dark:text-white">
                    <div className="font-medium">{formatDate(selectedTransaction.createdAt).date}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{formatDate(selectedTransaction.createdAt).time}</div>
                  </div>
                </div>
              </div>

              {/* Description */}
              {selectedTransaction.description && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Description</p>
                  <p className="text-gray-900 dark:text-white">{selectedTransaction.description}</p>
                </div>
              )}

              {/* Message Content */}
              {selectedTransaction.messageContent && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Customer Message</p>
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                      {selectedTransaction.messageContent}
                    </p>
                  </div>
                </div>
              )}

              {/* Generated Response */}
              {selectedTransaction.generatedResponse && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Generated Response</p>
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                    <p className="text-gray-900 dark:text-white whitespace-pre-wrap">
                      {selectedTransaction.generatedResponse}
                    </p>
                  </div>
                </div>
              )}

              {/* Comment ID */}
              {selectedTransaction.commentId && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Comment ID</p>
                  <p className="text-gray-900 dark:text-white font-mono text-sm">{selectedTransaction.commentId}</p>
                </div>
              )}

              {/* Metadata */}
              {selectedTransaction.metadata && Object.keys(selectedTransaction.metadata).length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Additional Information</p>
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <pre className="text-sm text-gray-900 dark:text-white overflow-x-auto">
                      {JSON.stringify(selectedTransaction.metadata, null, 2)}
                    </pre>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={closeModal}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-all duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}